<script setup lang="ts">
import { ref, computed } from 'vue';
import { checkLanguage } from '@/utils/check-language';
import SvgIcon from '@/presentation/components/common/svg-icon/SvgIcon.vue';
import { INPUT_LIMIT_LENGTH } from '@/constants'

const isFocus = ref<boolean>(false);

const props = defineProps<{
    originalInput: string
    placeholder: string,
    loadingSubmitBtn: boolean,
    noSupportLangTip: string,
    submitBtnText: string,
}>();

const emits = defineEmits<{
    (event: 'handleInput', input: string): void;
    (event: 'submit', input: string): void;
}>();

const isActive = computed(() => {
    return isFocus.value || props.originalInput !== '';
});

const isSupportLang = computed(() => {
    const { isSupportLang } = checkLanguage(props.originalInput);
    return isSupportLang;
});

function handleInput(event: Event) {
    const target = event.target as HTMLTextAreaElement;

    if (props.loadingSubmitBtn) return;
    emits('handleInput', target.value);
}

function handleSubmit() {
    emits('submit', props.originalInput);
}
</script>

<template>
    <div
        :class="[
            'mb-6 w-[640px] rounded-lg border border-eureka-200',
            {
                'lab-border-rounded-lg': isActive,
                'before:bg-red-500 before:bg-none': !isSupportLang,
            },
        ]"
    >
        <textarea
            :value="originalInput"
            class="mt-4 !h-[240px] w-full resize-none rounded-lg border-none p-4 pb-2 pt-0 outline-none"
            :placeholder="placeholder"
            :maxlength="INPUT_LIMIT_LENGTH"
            :readonly="loadingSubmitBtn"
            @input="handleInput"
            @focus="isFocus = true"
            @blur="isFocus = false"
        />

        <div
            class="m-4 mt-0 flex justify-between"
            :class="{ '!justify-end': !originalInput }"
        >
            <slot v-if="!isSupportLang" name="footerStart">
                <div class="flex shrink-0 items-center">
                    <SvgIcon
                        class="th-btn-icon h-6 w-6 fill-yellow-500"
                        icon="SolidWarning"
                    />
                    <span>
                        {{ noSupportLangTip }}
                    </span>
                </div>
            </slot>
            <slot name="footerEnd">
                <div class="flex w-full justify-between">
                    <span class="p-1 text-base text-gray-90">
                        {{ originalInput?.length ?? 0 }}/{{ INPUT_LIMIT_LENGTH }}
                    </span>

                    <button
                        class="th-btn"
                        :class="{
                            'th-btn-disabled': !originalInput || !isSupportLang,
                        }"
                        @click="handleSubmit"
                    >
                        <span
                            v-if="loadingSubmitBtn"
                            class="th-loading th-loading-spinner th-loading-sm"
                        />
                        <span v-else>
                            {{ submitBtnText }}
                        </span>
                    </button>
                </div>
            </slot>
        </div>
    </div>
</template>
